import React, { useState, useEffect, useCallback } from 'react';
import {
  Form,
  Input,
  Button,
  Modal,
  Select,
  Upload,
  message,
  InputNumber,
  Row,
  Col
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import PhoneInput from '../common/PhoneInput';
import ConfirmModal from '../ConfirmModal';
import { useDispatch, useSelector } from 'react-redux';
import { createCandidate, addCandidateFile, clearCandidateError } from '../../redux/candidatesSlice';
import { createVacancyCandidate } from '../../redux/vacancyCandidatesSlice';

const { Option } = Select;

const CreateModal = ({
  isOpen,
  onCancel,
  vacancies = [],
  onSuccess,
  initialValues = {}
}) => {
  const dispatch = useDispatch();
  const { orgId } = useSelector((state) => state.auth);
  const {
    isLoading: candidatesLoading,
    message_code,
    duplicateCandidate
  } = useSelector((state) => state.candidates);
  const { isLoading: vacancyCandidatesLoading } = useSelector((state) => state.vacancyCandidates);

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState({});

  useEffect(() => {
    if (isOpen) {
      dispatch(clearCandidateError());
      if (initialValues) {
        form.setFieldsValue(initialValues);
      }

      // Сохраняем начальные значения формы
      setTimeout(() => {
        const formValues = form.getFieldsValue(true);
        setInitialFormValues(formValues);
        setHasUnsavedChanges(false);
      }, 0);
    }
  }, [isOpen, initialValues, form, dispatch]);

  const checkForChanges = useCallback(() => {
    const currentValues = form.getFieldsValue(true);
    return JSON.stringify(currentValues) !== JSON.stringify(initialFormValues) || fileList.length > 0;
  }, [form, initialFormValues, fileList]);

  const handleFormChange = useCallback(() => {
    setHasUnsavedChanges(checkForChanges());
  }, [checkForChanges]);

  useEffect(() => {
    if (message_code === 'CANDIDATE_IS_ALREADY_EXISTS' && duplicateCandidate) {
      const matchedFields = duplicateCandidate?.matched_fields || [];
      const currentFormValues = form.getFieldsValue();

      let duplicateMessage = 'Кандидат с такими данными уже существует:';
      if (matchedFields.includes('phone_number')) {
        duplicateMessage += `\n- Телефон: ${duplicateCandidate.phone_number}`;
      }
      if (matchedFields.includes('email')) {
        duplicateMessage += `\n- Email: ${duplicateCandidate.email}`;
      }

      message.error({
        content: duplicateMessage,
        duration: 8
      });

      const fieldErrors = [];

      if (matchedFields.includes('phone_number') &&
        currentFormValues.phone_number === duplicateCandidate.phone_number) {
        fieldErrors.push({
          name: 'phone_number',
          errors: ['Кандидат с таким телефоном уже существует']
        });
      }

      if (matchedFields.includes('email') &&
        currentFormValues.email === duplicateCandidate.email) {
        fieldErrors.push({
          name: 'email',
          errors: ['Кандидат с таким email уже существует']
        });
      }

      if (fieldErrors.length > 0) {
        form.setFields(fieldErrors);

        setTimeout(() => {
          const firstErrorField = fieldErrors[0];
          const fieldElement = document.querySelector(`[name="${firstErrorField.name}"]`);
          if (fieldElement) {
            fieldElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });

            fieldElement.focus();
          }
        }, 100);
      }

      setLoading(false);
    }
  }, [message_code, duplicateCandidate, form]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      const salaryRange = `${values.salary_from}-${values.salary_to} ${values.salary_currency}`;

      const candidateData = {
        ...values,
        salary_range: salaryRange
      };

      delete candidateData.salary_from;
      delete candidateData.salary_to;
      delete candidateData.salary_currency;

      const candidateResult = await dispatch(createCandidate({
        orgId: orgId,
        candidate: candidateData
      })).unwrap();

      const { vacancy_id } = values;

      const candidateId = candidateResult.data || candidateResult.id;

      if (message_code === 'CANDIDATE_IS_ALREADY_EXISTS') {
        return;
      }

      await dispatch(createVacancyCandidate({
        vacancyId: vacancy_id,
        candidateId: candidateId
      })).unwrap();

      if (fileList.length > 0) {
        const file = fileList[0].originFileObj;
        const formData = new FormData();
        formData.append("file", file);

        try {
          await dispatch(addCandidateFile({
            id: candidateId,
            organisationId: orgId,
            file: formData
          })).unwrap();
          message.success('Файл успешно загружен');
        } catch (fileError) {
          console.error('Ошибка при загрузке файла:', fileError);
          message.error('Не удалось загрузить файл');
        }
      }

      form.resetFields();
      setFileList([]);

      if (onSuccess) {
        onSuccess();
      }

      onCancel();
    } catch (error) {
      console.error('Form validation failed:', error);
      message.error('Ошибка при создании кандидата');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
    setHasUnsavedChanges(checkForChanges());
  };

  const handleModalCancel = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowConfirm(true);
    } else {
      onCancel();
    }
  }, [hasUnsavedChanges, onCancel]);

  const handleConfirmClose = useCallback((shouldClose) => {
    if (shouldClose) {
      onCancel();
    }
    setShowConfirm(false);
  }, [onCancel]);

  const handleEmailChange = (e) => {
    if (message_code === 'CANDIDATE_IS_ALREADY_EXISTS' &&
      duplicateCandidate?.matched_fields?.includes('email') &&
      e.target.value !== duplicateCandidate.email) {
      form.setFields([{
        name: 'email',
        errors: []
      }]);
    }
  };

  const handlePhoneChange = (value) => {
    if (message_code === 'CANDIDATE_IS_ALREADY_EXISTS' &&
      duplicateCandidate?.matched_fields?.includes('phone_number') &&
      value !== duplicateCandidate.phone_number) {
      form.setFields([{
        name: 'phone_number',
        errors: []
      }]);
    }
  };

  const beforeUpload = (file) => {
    const isAllowedFormat = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ].includes(file.type);

    if (!isAllowedFormat) {
      message.error('Вы можете загрузить только файлы PDF, DOC или DOCX!');
      return Upload.LIST_IGNORE;
    }

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('Файл должен быть меньше 5MB!');
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  const footerButtons = [
    <Button key="cancel" onClick={handleModalCancel}>
      Закрыть
    </Button>,
    <Button key="submit" type="primary" loading={loading || candidatesLoading || vacancyCandidatesLoading} onClick={handleSubmit}>
      Сохранить
    </Button>,
  ];

  if (!isOpen) return null;

  return (
    <>
      <Modal
        title="Добавить кандидата"
        open={isOpen}
        onCancel={handleModalCancel}
        width={1000}
        footer={footerButtons}
      >
        <Form form={form} layout="vertical" onFieldsChange={handleFormChange}>
          {/* Основная информация */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Основная информация
            </h3>

            <Form.Item
              name="full_name"
              label="ФИО"
              rules={[{ required: true, message: 'Введите ФИО кандидата' }]}
            >
              <Input placeholder="Иванов Иван Иванович" />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[{ type: 'email', message: 'Некорректный email' }]}
                >
                  <Input
                    placeholder="example@mail.com"
                    onChange={handleEmailChange}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="phone_number"
                  label="Телефон"
                  rules={[
                    { required: true, message: 'Введите телефон кандидата' },
                    {
                      pattern: /^(\+375\d{9}|\+7\d{10})$/,
                      message: 'Введите корректный номер телефона'
                    }
                  ]}
                >
                  <PhoneInput
                    placeholder="+375 (29) 123-45-67"
                    onChange={handlePhoneChange}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Вакансия и зарплата */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Вакансия и зарплата
            </h3>

            <Form.Item
              name="vacancy_id"
              label="Вакансия"
              rules={[{ required: true, message: 'Выберите вакансию' }]}
            >
              <Select
                showSearch
                optionFilterProp="children"
                placeholder="Выберите вакансию"
                notFoundContent="Нет доступных вакансий"
              >
                {vacancies
                  .filter((vacancy) => vacancy.has_funnel === true)
                  .map((vacancy) => (
                    <Option key={vacancy.id} value={vacancy.id}>
                      {vacancy.job_title || vacancy.position || 'Без названия'}
                    </Option>
                  ))}
              </Select>
            </Form.Item>

            <Form.Item label="Желаемый уровень дохода">
              <Input.Group compact>
                <Form.Item
                  name="salary_currency"
                  noStyle
                  rules={[{ required: true, message: 'Выберите валюту' }]}
                >
                  <Select placeholder="Валюта" style={{ width: '28%' }}>
                    <Option value="BYN">BYN</Option>
                    <Option value="RUB">RUB</Option>
                    <Option value="USD">USD</Option>
                    <Option value="EUR">EUR</Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  name="salary_from"
                  noStyle
                  rules={[{ required: true, message: 'Введите значение "от"' }]}
                >
                  <InputNumber
                    placeholder="От"
                    style={{ width: '35%', marginLeft: 8 }}
                    min={0}
                  />
                </Form.Item>
                <Form.Item
                  name="salary_to"
                  noStyle
                  rules={[{ required: true, message: 'Введите значение "до"' }]}
                >
                  <InputNumber
                    placeholder="До"
                    style={{ width: '35%', marginLeft: 8 }}
                    min={0}
                  />
                </Form.Item>
              </Input.Group>
            </Form.Item>
          </div>

          {/* Документы */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Документы
            </h3>

            <Form.Item
              name="resume_file"
              label="Резюме (файл)"
              valuePropName="fileList"
              getValueFromEvent={({ fileList }) => fileList}
              help="Допустимые форматы файлов: PDF, DOC, DOCX"
            >
              <Upload
                name="resume"
                fileList={fileList}
                beforeUpload={beforeUpload}
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx"
                maxCount={3}
              >
                <Button icon={<UploadOutlined />}>Загрузить резюме</Button>
              </Upload>
            </Form.Item>
          </div>
        </Form>
      </Modal>

      <ConfirmModal
        visible={showConfirm}
        onConfirm={handleConfirmClose}
        onCancel={() => setShowConfirm(false)}
        title="Подтверждение закрытия"
        message="Данные не будут сохранены. Продолжить?"
        okText="Продолжить"
        cancelText="Отмена"
        type="warning"
      />
    </>
  );
};

export default CreateModal;